import React, { useEffect, useLayoutEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { Form } from "react-bootstrap";
import { Controller } from "react-hook-form";
import Calendar from "react-calendar";
import { BsCalendar, BsChevronDown, BsChevronUp, BsXLg } from "react-icons/bs";
import { RequiredLabel } from "./RequiredLabel";
import updateInputWidth from "update-input-width";
import "./CustomDateTimePicker.scss";

const pad = (value: number) => (value < 10 ? `0${value}` : `${value}`);

const parseValue = (value: any): Date | null => {
  if (!value) return null;
  if (value instanceof Date) return Number.isNaN(value.getTime()) ? null : new Date(value);
  const parsed = new Date(typeof value === "string" ? value.replace(/Z$/, "") : value);
  return Number.isNaN(parsed.getTime()) ? null : parsed;
};

const serialize = (value: Date) =>
  `${value.getFullYear()}-${pad(value.getMonth() + 1)}-${pad(value.getDate())}T${pad(value.getHours())}:${pad(value.getMinutes())}`;

const parseTypedValue = (value: string): Date | null => {
  const match = value.trim().match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})\s+(\d{1,2}):(\d{2})\s*(AM|PM)$/i);
  if (!match) return null;
  const [, monthText, dayText, yearText, hourText, minuteText, periodText] = match;
  const month = Number(monthText);
  const day = Number(dayText);
  const year = Number(yearText);
  const hour = Number(hourText);
  const minute = Number(minuteText);
  if (month < 1 || month > 12 || day < 1 || day > 31 || hour < 1 || hour > 12 || minute < 0 || minute > 59) return null;
  const hour24 = (hour % 12) + (periodText.toUpperCase() === "PM" ? 12 : 0);
  const parsed = new Date(year, month - 1, day, hour24, minute, 0, 0);
  return parsed.getFullYear() === year && parsed.getMonth() === month - 1 && parsed.getDate() === day ? parsed : null;
};

const parseInputValue = (value: any): Date | null => {
  if (typeof value === "string") {
    if (!/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}/.test(value)) return parseTypedValue(value);
  }
  return parseValue(value);
};

const hours = Array.from({ length: 12 }, (_, index) => index + 1);
const minutes = Array.from({ length: 59 }, (_, index) => index + 1);
const periods = ["AM", "PM"];

type DateTimeParts = {
  month: string;
  day: string;
  year: string;
  hour: string;
  minute: string;
  period: string;
};

const emptyDateTimeParts = (): DateTimeParts => ({
  month: "",
  day: "",
  year: "",
  hour: "",
  minute: "",
  period: "",
});

const getDateTimeParts = (value: Date | null): DateTimeParts => {
  if (!value) return emptyDateTimeParts();
  return {
    month: pad(value.getMonth() + 1),
    day: pad(value.getDate()),
    year: `${value.getFullYear()}`,
    hour: pad(value.getHours() % 12 || 12),
    minute: pad(value.getMinutes()),
    period: value.getHours() >= 12 ? "PM" : "AM",
  };
};

const getTypedValue = ({ month, day, year, hour, minute, period }: DateTimeParts) => {
  if (![month, day, year, hour, minute, period].some(Boolean)) return "";
  return `${month}/${day}/${year} ${hour}:${minute} ${period}`;
};

const setDatePart = (current: Date | null, selected: Date) => {
  const next = current ? new Date(current) : new Date();
  next.setFullYear(selected.getFullYear(), selected.getMonth(), selected.getDate());
  next.setSeconds(0, 0);
  return next;
};

type SegmentInputProps = Omit<React.InputHTMLAttributes<HTMLInputElement>, "value"> & {
  value: string;
};

const SegmentInput = ({ value, placeholder, ...props }: SegmentInputProps) => {
  const inputRef = useRef<HTMLInputElement>(null);

  useLayoutEffect(() => {
    if (inputRef.current) updateInputWidth(inputRef.current);
  }, [placeholder, value]);

  useEffect(() => {
    if (!document.fonts) return undefined;
    const updateWidth = () => {
      if (inputRef.current) updateInputWidth(inputRef.current);
    };
    document.fonts.addEventListener("loadingdone", updateWidth);
    return () => document.fonts.removeEventListener("loadingdone", updateWidth);
  }, []);

  return <input {...props} ref={inputRef} placeholder={placeholder} value={value} />;
};

const TimeColumn = ({ values, selected, format, onSelect, label }: any) => {
  const listRef = useRef<HTMLDivElement>(null);
  const selectedRef = useRef<HTMLButtonElement>(null);

  useLayoutEffect(() => {
    if (!listRef.current || !selectedRef.current) return;
    listRef.current.scrollTop = selectedRef.current.offsetTop
      - (listRef.current.clientHeight - selectedRef.current.offsetHeight) / 2;
  }, [selected]);

  return (
    <div
      ref={listRef}
      className="linde-datetime-picker__time-column"
      role="listbox"
      aria-label={label}
      tabIndex={0}
      onWheel={(event) => event.stopPropagation()}
    >
      {values.map((value: any, index: number) => (
        <button
          key={value}
          ref={value === selected ? selectedRef : undefined}
          type="button"
          role="option"
          aria-selected={value === selected}
          aria-posinset={index + 1}
          aria-setsize={values.length}
          className={value === selected ? "is-selected" : undefined}
          onClick={() => onSelect(value)}
        >
          {format(value)}
        </button>
      ))}
    </div>
  );
};

const EditableDateTimeInput = ({ value, onChange, disabled, className, error, open, setOpen }: any) => {
  const [parts, setParts] = useState<DateTimeParts>(() => getDateTimeParts(parseInputValue(value)));
  const focused = useRef(false);

  useEffect(() => {
    if (!focused.current) setParts(getDateTimeParts(parseInputValue(value)));
  }, [value]);

  const publish = (next: DateTimeParts, normalize = false) => {
    const typedValue = getTypedValue(next);
    const parsed = parseTypedValue(typedValue);
    onChange(parsed ? serialize(parsed) : typedValue);
    if (normalize && parsed) setParts(getDateTimeParts(parsed));
  };

  const updatePart = (name: keyof DateTimeParts, nextValue: string) => {
    const next = { ...parts, [name]: nextValue };
    setParts(next);
    publish(next);
  };

  const updateNumberPart = (name: keyof DateTimeParts, nextValue: string, maxLength: number) => {
    updatePart(name, nextValue.replace(/\D/g, "").slice(0, maxLength));
  };

  const handleKeyDown = (event: React.KeyboardEvent) => {
    if (event.key === "Enter") {
      event.preventDefault();
      publish(parts, true);
    } else if (event.key === "ArrowDown" && event.altKey) {
      event.preventDefault();
      setOpen(true);
    } else if (event.key === "Escape") {
      setOpen(false);
    }
  };

  return (
    <div
      className={[
        "linde-datetime-picker__input",
        "form-control",
        "form-control-sm",
        className,
        error && "is-invalid",
        disabled && "disabled",
      ].filter(Boolean).join(" ")}
      onFocus={() => { focused.current = true; }}
      onBlur={(event) => {
        if (event.currentTarget.contains(event.relatedTarget)) return;
        focused.current = false;
        publish(parts, true);
      }}
      onKeyDown={handleKeyDown}
      role="group"
      aria-label="Date and time"
      aria-invalid={!!error}
      aria-haspopup="dialog"
      aria-expanded={open}
    >
      <SegmentInput aria-label="Month" inputMode="numeric" maxLength={2} placeholder="mm" disabled={disabled} value={parts.month} onFocus={(event) => event.currentTarget.select()} onChange={(event) => updateNumberPart("month", event.target.value, 2)} />
      <span>/</span>
      <SegmentInput aria-label="Day" inputMode="numeric" maxLength={2} placeholder="dd" disabled={disabled} value={parts.day} onFocus={(event) => event.currentTarget.select()} onChange={(event) => updateNumberPart("day", event.target.value, 2)} />
      <span>/</span>
      <SegmentInput aria-label="Year" inputMode="numeric" maxLength={4} placeholder="yyyy" disabled={disabled} value={parts.year} onFocus={(event) => event.currentTarget.select()} onChange={(event) => updateNumberPart("year", event.target.value, 4)} />
      <span className="linde-datetime-picker__date-time-divider" />
      <SegmentInput aria-label="Hour" inputMode="numeric" maxLength={2} placeholder="hh" disabled={disabled} value={parts.hour} onFocus={(event) => event.currentTarget.select()} onChange={(event) => updateNumberPart("hour", event.target.value, 2)} />
      <span>:</span>
      <SegmentInput aria-label="Minute" inputMode="numeric" maxLength={2} placeholder="mm" disabled={disabled} value={parts.minute} onFocus={(event) => event.currentTarget.select()} onChange={(event) => updateNumberPart("minute", event.target.value, 2)} />
      <SegmentInput className="period" aria-label="AM or PM" maxLength={2} placeholder="AM" disabled={disabled} value={parts.period} onFocus={(event) => event.currentTarget.select()} onChange={(event) => updatePart("period", event.target.value.toUpperCase().replace(/[^APM]/g, "").slice(0, 2))} />
    </div>
  );
};

export const CustomDateTimePicker = ({ watch, editTableProps, field, control, required }: any) => {
  const disabled = field.disabled ? field.disabled({ watch, editTableProps }) : undefined;
  const rootRef = useRef<HTMLDivElement>(null);
  const popupRef = useRef<HTMLDivElement>(null);
  const [open, setOpen] = useState(false);
  const [popupStyle, setPopupStyle] = useState<React.CSSProperties>({});

  const positionPopup = () => {
    const bounds = rootRef.current?.getBoundingClientRect();
    if (!bounds) return;
    const width = Math.min(460, window.innerWidth - 16);
    const left = Math.max(8, Math.min(bounds.left, window.innerWidth - width - 8));
    const availableBelow = window.innerHeight - bounds.bottom;
    const top = availableBelow >= 330 ? bounds.bottom + 2 : Math.max(8, bounds.top - 324);
    setPopupStyle({ left, top, width });
  };

  useLayoutEffect(() => {
    if (open) positionPopup();
  }, [open]);

  useEffect(() => {
    if (!open) return undefined;
    const closeOnOutsideClick = (event: MouseEvent) => {
      const target = event.target as Node;
      if (!rootRef.current?.contains(target) && !popupRef.current?.contains(target)) setOpen(false);
    };
    const reposition = () => positionPopup();
    document.addEventListener("mousedown", closeOnOutsideClick);
    window.addEventListener("resize", reposition);
    window.addEventListener("scroll", reposition, true);
    return () => {
      document.removeEventListener("mousedown", closeOnOutsideClick);
      window.removeEventListener("resize", reposition);
      window.removeEventListener("scroll", reposition, true);
    };
  }, [open]);

  return (
    <Form.Group className="mb-1">
      {field.label && <RequiredLabel label={field.label} required={required} />}
      <Controller
        name={field.name}
        control={control}
        defaultValue=""
        rules={{
          required: required && "Please enter a valid date",
          validate: (value) => !value || !!parseInputValue(value) || "Please enter a valid date and time",
        }}
        render={({ field: { onChange, value }, fieldState: { error } }) => {
          const selected = parseInputValue(value);
          const update = (next: Date) => onChange(serialize(next));
          const selectHour = (hour: number) => {
            const next = selected ? new Date(selected) : new Date();
            const isPm = next.getHours() >= 12;
            next.setHours((hour % 12) + (isPm ? 12 : 0), next.getMinutes(), 0, 0);
            update(next);
          };
          const selectMinute = (minute: number) => {
            const next = selected ? new Date(selected) : new Date();
            next.setMinutes(minute, 0, 0);
            update(next);
          };
          const selectPeriod = (period: string) => {
            const next = selected ? new Date(selected) : new Date();
            const hour = next.getHours() % 12;
            next.setHours(hour + (period === "PM" ? 12 : 0), next.getMinutes(), 0, 0);
            update(next);
          };

          const popup = open && !disabled && (
            <div
              ref={popupRef}
              className="linde-datetime-picker__popup"
              style={popupStyle}
              role="dialog"
              aria-label={`${field.label || "Date and time"} picker`}
              onKeyDown={(event) => {
                if (event.key === "Escape") setOpen(false);
              }}
            >
              <div className="linde-datetime-picker__calendar">
                <Calendar
                  value={selected}
                  onChange={(date: any) => {
                    update(setDatePart(selected, date));
                    if (selected) setOpen(false);
                  }}
                  locale="en-US"
                  calendarType="gregory"
                  prev2Label={null}
                  next2Label={null}
                  prevLabel={<BsChevronUp aria-label="Previous month" />}
                  nextLabel={<BsChevronDown aria-label="Next month" />}
                />
                <div className="linde-datetime-picker__calendar-actions">
                  <button type="button" onClick={() => onChange("")}>Clear</button>
                  <button type="button" onClick={() => update(new Date())}>Today</button>
                  <button type="button" className="linde-datetime-picker__done" onClick={() => setOpen(false)}>Done</button>
                </div>
              </div>
              <div className="linde-datetime-picker__time" aria-label="Time">
                <TimeColumn
                  values={hours}
                  selected={(selected?.getHours() || 0) % 12 || 12}
                  format={pad}
                  onSelect={selectHour}
                  label="Hour"
                />
                <TimeColumn
                  values={minutes}
                  selected={selected?.getMinutes() ?? 0}
                  format={pad}
                  onSelect={selectMinute}
                  label="Minute"
                />
                <TimeColumn
                  values={periods}
                  selected={(selected?.getHours() || 0) >= 12 ? "PM" : "AM"}
                  format={(period: string) => period}
                  onSelect={selectPeriod}
                  label="AM or PM"
                />
              </div>
            </div>
          );

          return (
            <>
              <div ref={rootRef} className="linde-datetime-picker">
                <EditableDateTimeInput
                  value={value}
                  onChange={onChange}
                  disabled={disabled}
                  className={field.className}
                  error={error}
                  open={open}
                  setOpen={setOpen}
                />
                {!disabled && (
                  <button
                    type="button"
                    className="linde-datetime-picker__button linde-datetime-picker__clear"
                    aria-label="Clear date and time"
                    onClick={() => {
                      onChange("");
                      setOpen(false);
                    }}
                  >
                    <BsXLg />
                  </button>
                )}
                <button
                  type="button"
                  className="linde-datetime-picker__button linde-datetime-picker__trigger"
                  aria-label="Choose date and time"
                  disabled={disabled}
                  onClick={() => setOpen((current) => !current)}
                >
                  <BsCalendar />
                </button>
              </div>
              {error && (
                <Form.Control.Feedback type="invalid" className="d-block">
                  {error.message || "this field is required"}
                </Form.Control.Feedback>
              )}
              {typeof document !== "undefined" && popup ? createPortal(popup, document.body) : null}
            </>
          );
        }}
      />
    </Form.Group>
  );
};

export default CustomDateTimePicker;